import React, { useState, useEffect, useCallback } from 'react';
import { usePaymentService } from "../../services/payments/PaymentServiceProvider";
import { AgGridReact } from 'ag-grid-react';
import { ColDef, RowClassParams } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
    Button,
    Alert,
    Space,
    Upload,
    Card,
    Typography,
    message as antdMessage,
    Modal,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { DirectDebitRun } from "../../model/payments/DirectDebitRun";
import { DirectDebitRunPayment } from "../../model/payments/DirectDebitRunPayment";
import { DirectDebitRejection } from "../../model/payments/DirectDebitRejection";
import { ProcessDirectDebitRejectionRequest } from '../../requests/payments/ProcessDirectDebitRejectionRequest';
import { CompleteDirectDebitRunRequest } from '../../requests/payments/CompleteDirectDebitRunRequest';
import './DirectDebitRunManager.css';
import {AlertMessage} from "../common/AlertMessage";
import { useFormatCurrency } from "../../hooks/useFormatters";
import { DirectDebitRunSelect } from "./DirectDebitRunSelect";

const { Title } = Typography;

const DirectDebitRunManager: React.FC = () => {
    const paymentService = usePaymentService();
    const formatCurrency = useFormatCurrency();

    const [directDebitRuns, setDirectDebitRuns] = useState<DirectDebitRun[]>([]);
    const [selectedRun, setSelectedRun] = useState<DirectDebitRun | null>(null);
    const [rowData, setRowData] = useState<DirectDebitRunPayment[]>([]);
    const [message, setMessage] = useState<AlertMessage | null>(null);
    const [file, setFile] = useState<File | null>(null);
    const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);

    // Returns the fresh runs so callers can re-select from them; reading
    // `directDebitRuns` straight after the await sees the pre-refresh state.
    // Ordering belongs to the selector (DirectDebitRunSelect), which groups by
    // status before sorting — the sort that used to live here compared
    // getDate(), i.e. day-of-month, so runs from different months interleaved.
    const fetchDirectDebitRuns = useCallback(async () => {
        const runs = await paymentService.getDirectDebitRuns();
        setDirectDebitRuns(runs);
        return runs;
    }, [paymentService]);

    useEffect(() => {
        setMessage(null);

        // showError rethrows after notifying; without a catch the failure also lands as an
        // unhandled rejection.
        fetchDirectDebitRuns().catch(() => { /* notified by the API client */ });
    }, [fetchDirectDebitRuns]);

    const currencyFormatter = (params: { value: number }) => {
        return formatCurrency(params.value);
    };

    const columnDefs: ColDef[] = [
        { headerName: 'Membership ID', field: 'membershipId' },
        { headerName: 'Amount', field: 'amount', valueFormatter: currencyFormatter },
        { headerName: 'Reference', field: 'reference' },
        {
            headerName: 'Payment Status',
            field: 'paymentStatus',
            editable: true,
            cellEditor: 'agSelectCellEditor',
            cellEditorParams: {
                values: ['Pending', 'Completed', 'Failed', 'Cancelled', 'Rejected'],
            },
        },
        { headerName: 'Name', field: 'accountHolderName' },
    ];

    const getRowClass = (params: RowClassParams): string => {
        switch (params.data.paymentStatus) {
            case 'Pending':
                return 'bg-yellow';
            case 'Completed':
                return 'bg-green';
            case 'Failed':
                return 'bg-red';
            case 'Cancelled':
                return 'bg-grey';
            case 'Rejected':
                return 'bg-light-red';
            default:
                return '';
        }
    };

    // Re-selects the run we were on from a freshly fetched list, keeping the
    // grid rows on the same source as the selection.
    const selectRefreshedRun = (runs: DirectDebitRun[], runId: string) => {
        const refreshed = runs.find(run => run.runId === runId) ?? null;
        setSelectedRun(refreshed);
        setRowData(refreshed?.payments ?? []);
    };

    const handleRunChange = (run: DirectDebitRun) => {
        setSelectedRun(run);
        setRowData(run.payments);
    };

    const handleProcessRejections = async (info: any) => {
        const file = info.file.originFileObj;
        if (file) {
            setFile(file);
            setUploadedFileName(file.name);
            const rejections = await processRejectionFile(file);
            if (rejections.length === 0) {
                antdMessage.info('No rejections found.');
                setMessage({ type: 'info', text: 'No rejections found.' });
            } else {
                const updatedRowData = rowData.map(row => {
                    const rejection = rejections.find(r => r.reference === row.reference);
                    if (rejection) {
                        return { ...row, paymentStatus: rejection.status };
                    }
                    row.paymentStatus = 'Completed';
                    return row;
                });
                setRowData(updatedRowData);
            }
        }
    };

    const processRejectionFile = (file: File): Promise<DirectDebitRejection[]> => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                const content = e.target?.result as string;
                const lines = content.split('\n').slice(2); // Skip header line
                //Originating Account Name	Originating Account Branch Name	Originating Account Bank Name	 "Originating Account Type"	 "Originating Account Sort Code"	 "Originating Account Number"	
                // Reference	Currency	Amount	Payer Name	Payer Sort Code	Payer Account Number	Payer Account Branch Name	 "Payer Account Bank Name"	 "Payer Account Ref"	Transaction Code	
                // Return Code	Return Description	Original Processing Date
                const rejections = lines.map((line: string) => {
                    // Positional CSV columns (see header comment above). Only the
                    // fields used below are pulled out by index.
                    const cols = line.split(',');
                    const reference = cols[6];
                    const amount = cols[8];
                    const accountHolderName = cols[9];
                    const sortCode = cols[10];
                    const accountNumber = cols[11];
                    const status = cols[16];
                    if (!sortCode) {
                        return null;
                    }
                    const sanitizedSortCode = sortCode ?sortCode.replaceAll('-', '').replaceAll('"','') : '';
                    
                     return {
                        accountHolderName: accountHolderName.replaceAll('"', '').trim(),
                        sortCode:sanitizedSortCode,  // Remove any dashes from the sort code
                        accountNumber: accountNumber.replaceAll('"', '').trim(),
                        reference: reference.replaceAll('"', '').trim(),
                        amount: parseFloat(amount.replaceAll('"', '').trim()),
                        status: status.replaceAll('"', '').trim(),
                        paymentDate: selectedRun?.paymentDate ?? new Date()
                    } as DirectDebitRejection;
                });
                 resolve(rejections.filter(r => r !== null) as DirectDebitRejection[]);
            };
            reader.readAsText(file);
        });
    };

    const processRejections = async () => {
        if (!selectedRun || !file) return;

        const rejections = await processRejectionFile(file);

        console.log('Processing rejections:', rejections);
        const request: ProcessDirectDebitRejectionRequest = {
            runId: selectedRun.runId,
            rejections: rejections
        };

        try {
            const runId = selectedRun.runId
            console.log('Processing rejections:', request);
            await paymentService.processDirectDebitRejection(request);
            const runs = await fetchDirectDebitRuns();
            setMessage({ type: 'success', text: 'Direct Debit Rejections Processed' });
            antdMessage.success('Rejections processed successfully.');
            // The grid still holds the statuses guessed from the rejections file
            // when it was uploaded; re-seat it from the server's version of the
            // run so the rows can't disagree with the header and buttons.
            selectRefreshedRun(runs, runId);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error processing Direct Debit Run: ' + error });
        }
    };

    const handleMarkRunAsCompleted = async () => {
        if (!selectedRun) return;

        if (selectedRun.status !== 'RejectionsProcessed') {
            Modal.confirm({
                title: 'Confirm Completion',
                content: 'Are you sure you want to complete and roll over members without processing any rejections?',
                okText: 'Yes',
                cancelText: 'No',
                onOk: async () => {
                    await updateRunStatus('Completed');
                }
            });
        } else {
            await updateRunStatus('Completed');
        }
    };

    const updateRunStatus = async (newStatus: string) => {
        if (!selectedRun) return;

        const currentRunId = selectedRun.runId;
        const request: CompleteDirectDebitRunRequest = {
            runId: currentRunId,
            runStatus: newStatus,
            paymentStatus: 'Completed'  
        };

        try {
            await paymentService.completeDirectDebitRun(request);
            const runs = await fetchDirectDebitRuns();
            setMessage({ type: 'success', text: 'Direct debit run status updated successfully' });
            antdMessage.success('Direct debit run status updated successfully');
            selectRefreshedRun(runs, currentRunId);
        } catch (error) {
            setMessage({ type: 'error', text: 'Error updating direct debit run status: ' + error });
            antdMessage.error('Failed to update direct debit run status');
        }
    };

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Title level={2}>Direct Debit Run Manager</Title>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={4} style={{ marginTop: 0 }}>Select Direct Debit Run</Title>
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space>
                            <DirectDebitRunSelect
                                runs={directDebitRuns}
                                selectedRun={selectedRun}
                                onSelect={handleRunChange}
                            />
                            {selectedRun && (
                                <Typography.Text>
                                    Payment Date: {new Date(selectedRun.paymentDate).toDateString()}
                                </Typography.Text>
                            )}
                        </Space>
                        {selectedRun && (
                            <Button
                                data-testid="dd-mark-run-completed"
                                type="primary"
                                onClick={handleMarkRunAsCompleted}
                                disabled={selectedRun.status === 'Completed'}
                            >
                                Mark Run as Completed
                            </Button>
                        )}
                    </Space>
                </Space>
            </Card>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Title level={4} style={{ marginTop: 0 }}>Rejections</Title>

                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                        <Space align="center">
                            <Upload
                                accept=".csv"
                                beforeUpload={(file) => {
                                    handleProcessRejections({ file: { originFileObj: file } });
                                    return false;  // Prevent default upload behavior
                                }}
                                showUploadList={false}
                            >
                                <Button icon={<UploadOutlined />}>Choose Rejections File</Button>
                            </Upload>
                            <Typography.Text>
                                {uploadedFileName
                                    ? `File selected: ${uploadedFileName}`
                                    : 'No file chosen'}
                            </Typography.Text>
                        </Space>

                        <Space>
                            <Button
                                data-testid="dd-process-rejections"
                                type="primary"
                                onClick={processRejections}
                                disabled={!file}
                            >
                                Process Rejections
                            </Button>
                        </Space>
                    </Space>
                </Space>
            </Card>

            <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                <AgGridReact
                    columnDefs={columnDefs}
                    rowData={rowData}
                    getRowClass={getRowClass}
                    defaultColDef={{
                        flex: 1,
                        minWidth: 100,
                        filter: true,
                        sortable: true,
                        resizable: true,
                    }}
                />
            </div>

            {message && (
                <Alert
                    type={message.type}
                    message={message.text}
                    showIcon
                />
            )}
        </Space>
    );
};

export default DirectDebitRunManager;